"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AGENT_DIRECT_USER_INTERACTION_TOOL_NAMES = exports.AGENT_NEXT_USER_MESSAGE_TOOL_NAMES = exports.AGENT_ANSWER_TAG_TOOL_NAMES = void 0;
exports.getAgentInteractionToolName = getAgentInteractionToolName;
exports.getAgentInteractiveReplyMode = getAgentInteractiveReplyMode;
exports.isDefaultInteractiveRuntimeStopToolName = isDefaultInteractiveRuntimeStopToolName;
exports.shouldStopAfterInteractiveToolResult = shouldStopAfterInteractiveToolResult;
exports.hasUnresolvedAgentInteraction = hasUnresolvedAgentInteraction;
/**
 * Direct-user cards that submit through `<ANSWER toolCallId="…">`.
 * Add every new `onSubmitAnswer` tool here. `presentBudgetForApproval` is
 * intentionally absent: it belongs to the managed initiative approval rail.
 */
exports.AGENT_ANSWER_TAG_TOOL_NAMES = [
    'askQuestions',
    'askInputs',
    'searchApps',
    'promptSubscriptionUpgrade',
    'promptStaticLogoPolishing',
    'tiktokConfirmPostSettings',
    'collectBrandOnboardingInfo',
    'collectBrandStatusQuo',
    'selectOwnBrand',
    'selectAdvertiserBrands',
    'collectWinnerAds',
    'collectFunnelPersonas',
    'confirmBrandLogos',
    'confirmBrandColors',
    'askForBrandLogo',
    'askForCreativeAsset',
];
exports.AGENT_NEXT_USER_MESSAGE_TOOL_NAMES = ['searchProducts', 'connectIntegration'];
exports.AGENT_DIRECT_USER_INTERACTION_TOOL_NAMES = [
    ...exports.AGENT_ANSWER_TAG_TOOL_NAMES,
    ...exports.AGENT_NEXT_USER_MESSAGE_TOOL_NAMES,
];
const ANSWER_TAG_TOOL_NAMES = new Set(exports.AGENT_ANSWER_TAG_TOOL_NAMES);
const NEXT_USER_MESSAGE_TOOL_NAMES = new Set(exports.AGENT_NEXT_USER_MESSAGE_TOOL_NAMES);
/** These tools have additional mode-specific or loop-cap stop choreography. */
const NON_DEFAULT_RUNTIME_STOP_TOOL_NAMES = new Set([
    'collectBrandOnboardingInfo',
    'collectBrandStatusQuo',
    'promptSubscriptionUpgrade',
    'promptStaticLogoPolishing',
]);
function asRecord(value) {
    return value !== null && typeof value === 'object' ? value : null;
}
function isSuccessfulConnectionRequirement(output) {
    const record = asRecord(output);
    return record?.success === true && record.requiresSignIn === true;
}
/** Resolve static (`tool-name`) and dynamic (`dynamic-tool` + `toolName`) UI part names. */
function getAgentInteractionToolName(part) {
    if (typeof part.type === 'string' && part.type.startsWith('tool-'))
        return part.type.slice('tool-'.length);
    if (part.type === 'dynamic-tool' && typeof part.toolName === 'string')
        return part.toolName;
    return null;
}
/**
 * Return how an actionable tool result is resolved, or null when the result does
 * not currently require user action.
 */
function getAgentInteractiveReplyMode(toolName, output) {
    if (isSuccessfulConnectionRequirement(output))
        return 'next-user-message';
    const record = asRecord(output);
    if (record?.success === false)
        return null;
    switch (toolName) {
        case 'searchApps':
            return record?.success === true && Array.isArray(record.results) && record.results.length > 0
                ? 'answer-tag'
                : null;
        case 'searchProducts': {
            const meta = asRecord(record?.meta);
            return record?.success === true && meta?.selectionRequired === true ? 'next-user-message' : null;
        }
        case 'promptSubscriptionUpgrade':
            return record?.stillBlocked !== false ? 'answer-tag' : null;
        case 'promptStaticLogoPolishing':
            return record?.alreadyEnabled !== true ? 'answer-tag' : null;
        default:
            if (ANSWER_TAG_TOOL_NAMES.has(toolName))
                return 'answer-tag';
            if (NEXT_USER_MESSAGE_TOOL_NAMES.has(toolName))
                return 'next-user-message';
            return null;
    }
}
/** Known tools whose missing result still ends the turn fail-closed. */
function isDefaultInteractiveRuntimeStopToolName(toolName) {
    if (NON_DEFAULT_RUNTIME_STOP_TOOL_NAMES.has(toolName))
        return false;
    return ANSWER_TAG_TOOL_NAMES.has(toolName) || NEXT_USER_MESSAGE_TOOL_NAMES.has(toolName);
}
/** Output-aware default runtime stop decision for a completed tool result. */
function shouldStopAfterInteractiveToolResult(toolName, output) {
    if (NON_DEFAULT_RUNTIME_STOP_TOOL_NAMES.has(toolName))
        return false;
    return getAgentInteractiveReplyMode(toolName, output) !== null;
}
function collectAnsweredToolCallIds(messages) {
    const answered = new Set();
    const answerIdPattern = /toolCallId="([^"]+)"/g;
    for (const message of messages) {
        if (message.role !== 'user')
            continue;
        for (const part of message.parts ?? []) {
            if (part.type !== 'text' || typeof part.text !== 'string')
                continue;
            const text = part.text;
            for (const match of text.matchAll(answerIdPattern)) {
                if (match[1])
                    answered.add(match[1]);
            }
        }
    }
    return answered;
}
/**
 * Whether the transcript still contains a direct-user interaction awaiting resolution.
 * When `replyMode` is provided, only interactions using that response mechanism contribute.
 */
function hasUnresolvedAgentInteraction(messages, replyMode) {
    const answeredToolCallIds = collectAnsweredToolCallIds(messages);
    let hasNewerUserMessage = false;
    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (message.role === 'user') {
            hasNewerUserMessage = true;
            continue;
        }
        if (message.role !== 'assistant')
            continue;
        for (const part of message.parts ?? []) {
            if (part.state === 'output-error' || part.state === 'output-denied')
                continue;
            const toolCallId = typeof part.toolCallId === 'string' ? part.toolCallId : null;
            if (part.state === 'approval-requested') {
                if (replyMode !== 'next-user-message' && (!toolCallId || !answeredToolCallIds.has(toolCallId)))
                    return true;
                continue;
            }
            const toolName = getAgentInteractionToolName(part);
            if (!toolName)
                continue;
            const partReplyMode = getAgentInteractiveReplyMode(toolName, part.output);
            if (replyMode && partReplyMode !== replyMode)
                continue;
            if (partReplyMode === 'next-user-message' && !hasNewerUserMessage)
                return true;
            if (partReplyMode === 'answer-tag' && toolCallId && !answeredToolCallIds.has(toolCallId))
                return true;
        }
    }
    return false;
}
